/**
 * viewportFrozenLayersImport — import-side orchestration for EXPORT-VP-FROZEN.
 *
 * Bridges the raw DXF document (which knows WHICH viewports carry our
 * proprietary EDIT_DWG_VP_OVERRIDES XDATA block) to the leaf name↔handle resolver
 * (`viewportFrozenLayers.ts`), which knows nothing about the DXF section layout.
 *
 * A viewport whose EDIT_DWG_VP_OVERRIDES block GOVERNS the frozen-layer set (it
 * carries frozen-layer `1000` groups) AND was written by a LEGACY (pre-consistent-
 * 331) exporter has that NAME list as the authoritative freeze truth and takes the
 * PRUNE path: a stale 331 copied verbatim by an older exporter after a VPLAYER thaw
 * must NOT re-freeze a thawed layer. Every other viewport takes the MERGE path,
 * where the native group-331 handles are resolved to names:
 *   - NO owning block (none at all, OR a scale/lock/UCS-only block) — foreign/native
 *     source, the 331 handles are the ONLY frozen-layer source; AND
 *   - a v3+ TRUSTWORTHY-NATIVE owning block — a consistent-331 writer never leaves a
 *     stale post-thaw 331, so an unlisted resolvable handle is an EXTERNAL add to
 *     merge, not a stale handle to prune (@codex #486 P2).
 * `collectXdataAuthoritativeVpHandles` collects ONLY the legacy-authoritative
 * (prune-eligible) viewports; see `resolveViewportFrozenLayerNames` for the regimes
 * and `VP_OVERRIDES_CONSISTENT_NATIVE_331_VERSION` for the writer-provenance line.
 */

import {
  getBlocksSection,
  iterateSupportedEntities,
  type DxfDocument,
  type DxfSupportedEntity,
} from "@dxf-ai-editor/dxf-io";

import {
  resolveViewportFrozenLayerNames,
  type FrozenLayerRef,
} from "./viewportFrozenLayers";
import {
  readViewportOverrides,
  VP_OVERRIDES_CONSISTENT_NATIVE_331_VERSION,
} from "./viewportOverrides";

/** The `spaces` shape the leaf resolver mutates (kept exact via `Parameters`). */
type FrozenLayerSpaces = Parameters<typeof resolveViewportFrozenLayerNames>[0];

/**
 * Collect the set of viewport record handles whose EDIT_DWG_VP_OVERRIDES XDATA is
 * LEGACY-AUTHORITATIVE for the frozen-layer set — i.e. it carries frozen-layer
 * `1000` groups (owns the set) AND is written by a PRE-consistent-331 writer
 * (`writerVersion` absent or `< VP_OVERRIDES_CONSISTENT_NATIVE_331_VERSION`).
 * Handles are keyed lower-case to match the model entity id (= DXF record handle
 * on import). Only these viewports take the PRUNE path in
 * `resolveViewportFrozenLayerNames`; every other viewport (foreign, OR a v3+
 * trustworthy-native file) takes the MERGE path.
 *
 * WHY EXCLUDE v3+ (@codex #486 P2 "Do not discard native freezes added after our
 * export"): a v3+ writer keeps the native group-331 handles CONSISTENT with the
 * XDATA names on every export (a thaw positively drops the 331 handle; export
 * unions resolvable 331 names back into the XDATA), so a v3+ file can NEVER carry a
 * stale post-thaw 331. Therefore a resolvable 331 handle absent from a v3+ file's
 * XDATA names is NOT a legacy stale-thaw artifact but an EXTERNAL post-export ADD (a
 * native writer edited the 331 list and left our proprietary XDATA stale) — pruning
 * it against the stale names would silently discard a genuine per-viewport freeze.
 * Excluding v3+ files here routes them to the MERGE path, which preserves the add.
 * The PRUNE path stays for legacy/pre-marker files, where an older exporter really
 * could have copied a stale 331 verbatim after a thaw (@codex #486 P2 "Honor XDATA
 * freezes before merging native handles") — trusting a legacy 331 as an add would
 * re-freeze a thawed layer, so those stay conservatively pruned.
 *
 * MUST run on the RAW DXF document BEFORE any draining pass. In the streaming
 * worker path `buildImportedSpaces` is invoked with `drainTopLevelEntities: true`
 * and CONSUMES/CLEARS the top-level ENTITIES array; a post-drain scan would then
 * miss every top-level (paper-space) VIEWPORT and drop its XDATA authority, so
 * `resolveViewportFrozenLayerNames` would treat those viewports as foreign and
 * merge stale native 331 handles back into `frozenLayers` (@codex #486 P2). The
 * bridge therefore calls this ONCE up front (mirroring `collectUnsupportedEntity
 * Groups`) and passes the resulting set through to `resolveImportedVpFrozen`.
 *
 * Scans BOTH the top-level ENTITIES section AND block definitions (paper-space
 * blocks) so every routed viewport matches. Uses the LAZY, non-draining
 * `iterateSupportedEntities` (NOT `[...getSupportedEntities(document)]`) so the
 * pre-drain authority scan does NOT materialize a full filtered array of every
 * supported top-level entity — that transient allocation would reintroduce the
 * peak-memory cost the streaming drain was designed to avoid on 685k-entity
 * drawings (@codex #486 P2 "materializing streamed entities before drain"). Only
 * the tiny VIEWPORT-handle Set is retained.
 */
export function collectXdataAuthoritativeVpHandles(
  document: DxfDocument,
): Set<string> {
  const handles = new Set<string>();
  const consider = (entity: DxfSupportedEntity): void => {
    if (entity.type !== "VIEWPORT") return;
    const handle = entity.common?.handle?.trim();
    if (!handle) return;
    // Read the block ONCE for both signals (single source of truth for the block
    // layout). `hasFrozenLayerGroups` = the block CARRIES frozen-layer `1000`
    // groups; `writerVersion` = the `1071` marker after the scale codes.
    const overrides = readViewportOverrides({
      xdata: entity.xdata as
        | { appName: string; groups: { code: number; value: string }[] }[]
        | undefined,
    });
    // Authority-to-PRUNE requires BOTH:
    //   (1) the block actually OWNS the freeze set — a bare appid (scale/lock/UCS-
    //       only block) does NOT own it and must not trigger the prune path
    //       (@codex #486 P1), AND
    //   (2) the file is LEGACY (pre-consistent-331 writer): a v3+ file's native 331
    //       is trustworthy, so an unlisted resolvable handle is an external ADD to
    //       MERGE, not a stale handle to prune (@codex #486 P2 — see fn doc).
    const isLegacyWriter =
      overrides.writerVersion === undefined ||
      overrides.writerVersion < VP_OVERRIDES_CONSISTENT_NATIVE_331_VERSION;
    if (overrides.hasFrozenLayerGroups && isLegacyWriter) {
      handles.add(handle.toLowerCase());
    }
  };
  // Top-level ENTITIES — iterated LAZILY (no full-array materialization).
  for (const entity of iterateSupportedEntities(document)) consider(entity);
  // Block definitions (paper-space blocks) — already small, iterated in place.
  const blocks = getBlocksSection(document);
  if (blocks) {
    for (const block of blocks.blocks) {
      for (const child of block.entities) {
        if (child.kind === "supported-entity") consider(child);
      }
    }
  }
  return handles;
}

/**
 * Resolve/reconcile every routed viewport's frozen layers on import, using the
 * PRE-COLLECTED authoritative-XDATA handle set (built by
 * `collectXdataAuthoritativeVpHandles` BEFORE the streaming drain — see its doc)
 * and handing it to the leaf resolver.
 */
export function resolveImportedVpFrozen(
  spaces: FrozenLayerSpaces,
  layers: readonly FrozenLayerRef[],
  xdataAuthoritativeVpHandles: ReadonlySet<string>,
): void {
  resolveViewportFrozenLayerNames(spaces, layers, xdataAuthoritativeVpHandles);
}
